import { useState } from 'react'

import { api, ErrorApi, type FuenteConsumo, type Proyeccion } from '../api'
import { leerNumero } from '../formato'

/**
 * De donde sale el consumo de la cotizacion: escrito a mano o el promedio de los 12
 * meses que proyecta el motor con el historial del cliente (en industrial, tambien la
 * demanda). El servidor vuelve a proyectar al calcular y al guardar: lo que se escribe
 * aqui es solo para verlo, no se confia en el.
 */

interface Props {
  clienteId: string
  industrial: boolean
  fuente: FuenteConsumo
  alCambiarFuente: (fuente: FuenteConsumo) => void
  alProyectar: (kwh: number, kw: number | null) => void
}

const resumen = (p: Proyeccion, unidad: string) =>
  `${(p.promedio ?? 0).toLocaleString('es-EC', { maximumFractionDigits: 1 })} ${unidad}/mes · ${
    p.metodo === 'estacional' ? 'modelo estacional' : 'promedio simple'
  } · error de backtest ${p.error_backtest_pct === null ? '—' : p.error_backtest_pct.toLocaleString('es-EC')} % · ${p.meses_historial} meses`

export function OrigenConsumo({ clienteId, industrial, fuente, alCambiarFuente, alProyectar }: Props) {
  const [detalle, setDetalle] = useState('')
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)

  const usarProyeccion = async () => {
    setError('')
    setCargando(true)
    try {
      const { proyeccion } = await api.proyectar(clienteId)
      const kwh = proyeccion.kwh
      const kw = proyeccion.demanda_kw
      if (!kwh.suficiente || kwh.promedio === null) throw new Error(kwh.explicacion)
      if (industrial && (!kw || !kw.suficiente || kw.promedio === null))
        throw new Error(kw ? `Demanda: ${kw.explicacion}` : 'El historial no tiene la demanda (kW) de cada mes: escríbela en la ficha del cliente.')
      alProyectar(kwh.promedio, industrial && kw ? kw.promedio : null)
      alCambiarFuente('proyeccion')
      setDetalle(resumen(kwh, 'kWh') + (industrial && kw ? ` — demanda ${resumen(kw, 'kW')}` : ''))
    } catch (err) {
      alCambiarFuente('manual')
      setError(err instanceof ErrorApi || err instanceof Error ? err.message : 'No se pudo proyectar.')
    } finally {
      setCargando(false)
    }
  }

  return (
    <div className="bloque-descuento origen-consumo">
      <span className="fuerte">Consumo:</span>
      <div className="filtros">
        <button type="button" className={fuente === 'manual' ? 'chip activo' : 'chip'} onClick={() => alCambiarFuente('manual')}>
          Escrito a mano
        </button>
        <button type="button" className={fuente === 'proyeccion' ? 'chip activo' : 'chip'} onClick={usarProyeccion} disabled={cargando}>
          {cargando ? 'Proyectando…' : 'Proyectado del historial'}
        </button>
      </div>
      {fuente === 'proyeccion' && detalle && <small className="ayuda">{detalle}</small>}
      {fuente === 'proyeccion' && !detalle && <small className="ayuda">Se recalcula con el historial guardado al calcular.</small>}
      {error && <small className="error-en-linea">{error}</small>}
    </div>
  )
}

/** Los dos supuestos del costo de esperar, en %, como se escriben. */
export interface TextoEspera {
  tarifa: string
  precio: string
}

export function leerEspera(t: TextoEspera): { subida_anual_tarifa: number; subida_anual_precio: number } | string {
  const tarifa = leerNumero(t.tarifa) ?? 0
  const precio = leerNumero(t.precio) ?? 0
  if (Number.isNaN(tarifa) || tarifa < 0 || tarifa > 50) return 'La subida anual de la tarifa va en %, entre 0 y 50.'
  if (Number.isNaN(precio) || precio < 0 || precio > 50) return 'La subida anual del precio del sistema va en %, entre 0 y 50.'
  return { subida_anual_tarifa: tarifa / 100, subida_anual_precio: precio / 100 }
}

export function SupuestosEspera({ valor, alCambiar }: { valor: TextoEspera; alCambiar: (v: TextoEspera) => void }) {
  return (
    <fieldset className="grupo-campos">
      <legend>Costo de esperar — supuestos</legend>
      <label>
        Subida anual de la tarifa (%)
        <input value={valor.tarifa} onChange={(e) => alCambiar({ ...valor, tarifa: e.target.value })} inputMode="decimal" />
      </label>
      <label>
        Subida anual del precio del sistema (%)
        <input value={valor.precio} onChange={(e) => alCambiar({ ...valor, precio: e.target.value })} inputMode="decimal" />
      </label>
      <small className="ayuda ancho">
        0 por defecto: la tarifa eléctrica está congelada y no hay fuente para la subida del precio. Si pones otro valor, sale escrito en la
        pantalla y en el PDF.
      </small>
    </fieldset>
  )
}
